import React, { useState } from 'react';
import { Upload, X, Image as ImageIcon } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import axios from 'axios';

const ImageUploader = ({ value, onChange, label = "Image" }) => {
  const [uploading, setUploading] = useState(false);
  const [showUrlInput, setShowUrlInput] = useState(false);

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      alert('Le fichier est trop volumineux (max 5MB)');
      return;
    }

    // Validate file type
    if (!file.type.startsWith('image/')) {
      alert('Veuillez sélectionner une image');
      return;
    }

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);

      const token = localStorage.getItem('admin_token');
      const response = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/upload`,
        formData,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          }
        }
      );

      if (response.data.success) {
        onChange(response.data.url);
        alert('Image téléchargée avec succès!');
      }
    } catch (error) {
      console.error('Upload error:', error);
      
      // Don't show generic error if it's a 401 (will be handled by interceptor)
      if (error.response?.status !== 401) {
        const errorMsg = error.response?.data?.detail || 'Erreur lors du téléchargement de l\'image';
        alert(errorMsg);
      }
    } finally {
      setUploading(false);
    }
  };

  const clearImage = () => {
    onChange('');
  };

  return (
    <div className="space-y-3">
      <Label>{label}</Label>
      
      {/* Image Preview */}
      {value && (
        <div className="relative inline-block">
          <img 
            src={value.startsWith('http') ? value : `${process.env.REACT_APP_BACKEND_URL}${value}`}
            alt="Preview" 
            className="w-full max-w-xs h-32 object-cover rounded-lg border-2 border-gray-200"
            onError={(e) => {
              e.target.style.display = 'none';
            }}
          />
          <button
            onClick={clearImage}
            className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600 transition-colors"
            type="button"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* Upload Button */}
      <div className="flex gap-2">
        <label className="flex-1">
          <input
            type="file"
            accept="image/*"
            onChange={handleFileUpload}
            className="hidden"
            disabled={uploading}
          />
          <Button
            type="button"
            variant="outline"
            className="w-full cursor-pointer"
            disabled={uploading}
            asChild
          >
            <span>
              {uploading ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-purple-600 mr-2"></div>
                  Téléchargement...
                </>
              ) : (
                <>
                  <Upload className="w-4 h-4 mr-2" />
                  Télécharger depuis mon ordinateur
                </>
              )}
            </span>
          </Button>
        </label>
        
        <Button
          type="button"
          variant="outline"
          onClick={() => setShowUrlInput(!showUrlInput)}
          title="Entrer une URL manuellement"
        >
          <ImageIcon className="w-4 h-4" />
        </Button>
      </div>

      {/* Manual URL Input (optional) */}
      {showUrlInput && (
        <div className="pt-2">
          <Input
            value={value || ''}
            onChange={(e) => onChange(e.target.value)}
            placeholder="Ou entrez une URL d'image..."
            className="text-sm"
          />
          <p className="text-xs text-gray-500 mt-1">
            Format: /uploads/image.jpg ou URL complète
          </p>
        </div>
      )}

      {/* Current URL Display */}
      {value && !showUrlInput && (
        <p className="text-xs text-gray-600 break-all">
          URL actuelle: {value}
        </p>
      )}
    </div>
  );
};

export default ImageUploader;
